import React, { memo, useContext, useEffect, useState } from "react";
import EditIcon from "@mui/icons-material/Edit";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import MensagemSucesso from "../../../../../components/MensagemSucesso";
import MensagemErro from "../../../../../components/MensagemErro";
import BlueButton from "../../../../../components/BlueButton";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import WhiteButton from "../../../../../components/WhiteButton";
import CheckIcon from "@mui/icons-material/Check";
import SendIcon from "@mui/icons-material/Send";
import { keyframes } from "@emotion/react";
import styles from "./styles.module.css";
import dayjs from "dayjs";
import InputAdornment from "@mui/material/InputAdornment";

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Fab,
  Grid,
  TextField,
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
} from "@mui/material";
import { debounce } from "lodash";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import SearchIcon from "@mui/icons-material/Search";
import TableCardv2 from "../../../../../components/TableCardv2";
import { PreviaInssContext } from "../../../PreviaInssContext";
import GenericDatePickerReference from "../../../../../components/GenericDatePickerReference";

const Impostos = memo(() => {
  const [dataTable, setDataTable] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [idImposto, setIdImposto] = useState(null);
  const [openSnackSucesso, setOpenSnackSucesso] = useState(false);
  const [openSnackErro, setOpenSnackErro] = useState(false);
  const [msg, setMsg] = useState(null);
  const [isEdit, setIsEdit] = useState(false);
  const [openModalInsert, setOpenModalInsert] = useState(false);
  const [openModalDelete, setOpenModalDelete] = useState(false);
  const [referencia, setReferencia] = useState("");
  const [referenciaInicio, setReferenciaInicio] = useState("");
  const [referenciaFim, setReferenciaFim] = useState("");
  const [terceiros, setTerceiros] = useState("");
  const [rat, setRat] = useState("");
  const [inss, setInss] = useState("");
  const { dados, setDados, handleAtualizarVersao, loadingVersao } = useContext(PreviaInssContext);

  // Helper to notify user when trying to mutate a non-editable version
  const showNotEditableMessage = () => {
    setMsg("Esta versão não permite alterações.");
    setOpenSnackErro(true);
  };

  const handleCloseSnackSucesso = (_, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setOpenSnackSucesso(false);
  };
  const handleCloseSnackErro = (_, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setOpenSnackErro(false);
  };

  const columns = [
    { key: "data", label: "Referência", width: "150px", align: "left" },
    { key: "terceiros", label: "Terceiros (%)", width: "150px", align: "center" },
    { key: "rat", label: "RAT (%)", width: "150px", align: "center" },
    { key: "inss", label: "Parte Emp. (%)", width: "150px", align: "center" },
    { key: "total", label: "Total (%)", width: "150px", align: "center" },
  ];

  // Calcula estatísticas para os cards de resumo
  const calcularEstatisticas = () => {
    if (!dados.impostos || dados.impostos.length === 0) {
      return {
        totalPeriodos: 0,
        mediaTerceiros: 0,
        mediaRat: 0,
        mediaInss: 0,
        mediaTotal: 0,
        periodoMaisAntigo: null,
        periodoMaisRecente: null,
      };
    }

    const impostos = dados.impostos;
    const totalPeriodos = impostos.length;
    
    const somaTerceiros = impostos.reduce((acc, item) => acc + (parseFloat(item.terceiros) || 0), 0);
    const somaRat = impostos.reduce((acc, item) => acc + (parseFloat(item.rat) || 0), 0);
    const somaInss = impostos.reduce((acc, item) => acc + (parseFloat(item.inss) || 0), 0);
    
    const mediaTerceiros = totalPeriodos > 0 ? (somaTerceiros / totalPeriodos).toFixed(2) : 0;
    const mediaRat = totalPeriodos > 0 ? (somaRat / totalPeriodos).toFixed(2) : 0;
    const mediaInss = totalPeriodos > 0 ? (somaInss / totalPeriodos).toFixed(2) : 0;
    
    // Média total = média da soma (terceiros + rat + inss) de cada período
    const somaTotaisPorPeriodo = impostos.reduce((acc, item) => {
      const terceiros = parseFloat(item.terceiros) || 0;
      const rat = parseFloat(item.rat) || 0;
      const inss = parseFloat(item.inss) || 0;
      return acc + (terceiros + rat + inss);
    }, 0);
    const mediaTotal = totalPeriodos > 0 ? (somaTotaisPorPeriodo / totalPeriodos).toFixed(2) : 0;

    const referencias = impostos.map(item => dayjs(item.referencia));
    const periodoMaisAntigo = referencias.length > 0 
      ? referencias.reduce((min, d) => d.isBefore(min) ? d : min).format("MM/YYYY")
      : null;
    const periodoMaisRecente = referencias.length > 0 
      ? referencias.reduce((max, d) => d.isAfter(max) ? d : max).format("MM/YYYY")
      : null;

    return {
      totalPeriodos,
      mediaTerceiros,
      mediaRat,
      mediaInss,
      mediaTotal,
      periodoMaisAntigo,
      periodoMaisRecente,
    };
  };

  const estatisticas = calcularEstatisticas();

  useEffect(() => {
    const fetchUpdate = async () => {
      const sortedImpostos = [...dados.impostos].sort((a, b) => 
        dayjs(b.referencia).unix() - dayjs(a.referencia).unix()
      );
      const responseOrganizada = handleOrganizaData(sortedImpostos);
      setDataTable(responseOrganizada);
  
      await handleAtualizarVersao({...dados, impostos: sortedImpostos });
    }
    fetchUpdate();
  }, [dados.impostos]);

  const handleOpenModalInsert = async () => {
    setOpenModalInsert(true);
  };

  const handleOpenModalDelete = async () => {
    setOpenModalDelete(true);
  };

  const handleCloseModalInsert = () => {
    setOpenModalInsert(false);
    setIdImposto(null);
    setReferenciaInicio({ content: null, error: false });
    setReferenciaFim({ content: null, error: false });
    setTerceiros({ content: null, error: false });
    setRat({ content: null, error: false });
    setInss({ content: null, error: false });
  };

  const handleCloseModalDelete = () => {
    setOpenModalDelete(false);
  };

  const handleOrganizaData = (dadosArr) => {
    return dadosArr.map((item) => {
      const terceirosVal = parseFloat(item.terceiros) || 0;
      const ratVal = parseFloat(item.rat) || 0;
      const inssVal = parseFloat(item.inss) || 0;
      const totalVal = terceirosVal + ratVal + inssVal;
      
      // Formatação visual discreta com chips do sistema
      const formatarPercentual = (valor) => (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
          <Chip
            label={`${valor}%`}
            size="small"
            variant="outlined"
            sx={{
              borderColor: "#18316e",
              color: "#18316e",
              fontWeight: 500,
              minWidth: "70px",
              height: "28px",
              backgroundColor: "transparent",
            }}
          />
        </Box>
      );
      
      return {
        id: item.idImposto,
        data: (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 500, fontSize: "0.9rem" }}>
              {dayjs(item.referencia).format("MM/YYYY")}
            </Typography>
          </Box>
        ),
        terceiros: formatarPercentual(item.terceiros),
        rat: formatarPercentual(item.rat),
        inss: formatarPercentual(item.inss),
        total: formatarPercentual(totalVal.toFixed(2)),
        btn: [
          {
            icon: <EditIcon />,
            title: "EDITAR",
            onClick: async () => {
              try {
                if (!dados.isEditable) {
                  showNotEditableMessage();
                  return;
                }
                setIsEdit(true);
                setIdImposto(item.idImposto);
                setReferencia(item.referencia);
                setTerceiros({ content: item.terceiros, error: false });
                setRat({ content: item.rat, error: false });
                setInss({ content: item.inss, error: false });
                handleOpenModalInsert();
              } catch (error) {
                console.log(error);
              }
            },
          },
          {
            icon: <DeleteForeverIcon />,
            title: "EXCLUIR",
            onClick: () => {
              if (!dados.isEditable) {
                showNotEditableMessage();
                return;
              }
              setIdImposto(item.idImposto);
              handleOpenModalDelete();
            },
          },
        ],
      };
    });
  };
  const handleDebounce = debounce((value) => {
    handlePesquisaPorTexto(value);
  }, 500);

  const handlePesquisaPorTexto = (value) => {
    if (!value) {
      const sortedImpostos = [...dados.impostos].sort((a, b) => 
        dayjs(b.referencia).unix() - dayjs(a.referencia).unix()
      );
      setDataTable(handleOrganizaData(sortedImpostos));
      return;
    }

    const searchValue = value.toLowerCase();
    const filtered = dados.impostos.filter(item => {
      const referenciaStr = dayjs(item.referencia).format("MM/YYYY");
      const terceirosStr = item.terceiros?.toString() || "";
      const ratStr = item.rat?.toString() || "";
      const inssStr = item.inss?.toString() || "";
      
      return (
        referenciaStr.toLowerCase().includes(searchValue) ||
        terceirosStr.toLowerCase().includes(searchValue) ||
        ratStr.toLowerCase().includes(searchValue) ||
        inssStr.toLowerCase().includes(searchValue)
      );
    });

    const sortedFiltered = filtered.sort((a, b) => 
      dayjs(b.referencia).unix() - dayjs(a.referencia).unix()
    );
    setDataTable(handleOrganizaData(sortedFiltered));
  };

  const handleSubmitInsert = (e) => {
    e.preventDefault();
    if (!dados.isEditable) {
      showNotEditableMessage();
      return;
    }

    setLoading(true);
  
    try {
      // parse start/end dates
      const start = dayjs(referenciaInicio?.content);
      const end = referenciaFim?.content ? dayjs(referenciaFim.content) : start;
  
      if (!start.isValid()) throw new Error("Data de início inválida");
      if (!end.isValid()) throw new Error("Data de fim inválida");
  
      // build list of months between start and end (inclusive)
      const months = [];
      let cur = start.startOf("month");
      const endMonth = end.startOf("month");
      while (cur.isBefore(endMonth) || cur.isSame(endMonth)) {
        months.push(cur.format("YYYY-MM"));
        cur = cur.add(1, "month");
      }
  
      const maxId = Math.max(0, ...dados.impostos.map(i => i.idImposto));
      const baseTerceiros = parseFloat(terceiros.content);
      const baseRat = parseFloat(rat.content);
      const baseInss = parseFloat(inss.content);
  
      const newImpostos = months.map((m, idx) => ({
        idImposto: maxId + idx + 1,
        referencia: m,
        terceiros: baseTerceiros,
        rat: baseRat,
        inss: baseInss,
      }));
  
      const updatedImpostos = [...dados.impostos, ...newImpostos]
        .sort((a, b) => dayjs(b.referencia).unix() - dayjs(a.referencia).unix());
  
      setDados(prev => ({
        ...prev,
        impostos: updatedImpostos
      }));
  
      setMsg("Imposto(s) adicionado(s) com sucesso!");
      setOpenSnackSucesso(true);
      handleCloseModalInsert();
    } catch (error) {
      console.error('Erro ao adicionar imposto(s):', error);
      setMsg("Erro ao adicionar imposto");
      setOpenSnackErro(true);
    } finally {
      setLoading(false);
    }
  };
  const handleSubmitUpdate = (e) => {
    e.preventDefault();
    if (!dados.isEditable) {
      showNotEditableMessage();
      return;
    }
    setLoading(true);

    try {
      setDados(prev => ({
        ...prev,
        impostos: prev.impostos.map(item => 
          item.idImposto === idImposto
            ? {
                ...item,
                terceiros: parseFloat(terceiros.content),
                rat: parseFloat(rat.content),
                inss: parseFloat(inss.content)
              }
            : item
        )
      }));

      setMsg("Imposto atualizado com sucesso!");
      setOpenSnackSucesso(true);
      handleCloseModalInsert();
    } catch (error) {
      setMsg("Erro ao atualizar imposto");
      setOpenSnackErro(true);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitDelete = (e) => {
    e.preventDefault();
    if (!dados.isEditable) {
      showNotEditableMessage();
      return;
    }
    setLoading(true);
    
    try {
      setDados(prev => ({
        ...prev,
        impostos: prev.impostos.filter(item => item.idImposto !== idImposto)
      }));

      setMsg("Imposto excluído com sucesso!");
      setOpenSnackSucesso(true);
      handleCloseModalDelete();
    } catch (error) {
      setMsg("Erro ao excluir imposto");
      setOpenSnackErro(true);
    } finally {
      setLoading(false);
    }
  };

  const handleChangeValue = (name, value) => {
    switch (name) {
      case "referencia":
        if (!isNaN(value.getTime())) {
          setReferencia({
            content: value,
            error: value != null ? false : true,
          });
        } else {
          setReferencia({
            content: value,
            error: true,
          });
        }
        break;
      case "terceiros":
        const maskedTerceiros = value.replace(/,/g, ".");
        setTerceiros({ content: maskedTerceiros, error: maskedTerceiros != null ? false : true });
        break;
      case "rat":
        const maskedRat = value.replace(/,/g, ".");
        setRat({ content: maskedRat, error: maskedRat != null ? false : true });
        break;
      case "inss":
        const maskedInss = value.replace(/,/g, ".");
        setInss({ content: maskedInss, error: maskedInss != null ? false : true });
        break;
      default:
        break;
    }
  };

  const handleChangeReferenciaInicio = (newValue) => {
    setReferenciaInicio({ content: newValue, error: false });
  };

  const handleChangeReferenciaFim = (newValue) => {
    setReferenciaFim({ content: newValue, error: false });
  };

  const ComplementaryHeaderTop = () => {
    return (
      <Button
        className={styles.btnAdd}
        startIcon={<AddCircleOutlineIcon />}
        size="medium"
        variant="outlined"
        disabled={!dados.isEditable}
        onClick={() => {
          if (!dados.isEditable) {
            showNotEditableMessage();
            return;
          }
          setIsEdit(false);
          handleOpenModalInsert();
        }}
        sx={{
          whiteSpace: "nowrap",
          minWidth: "auto",
          px: 1.5,
          fontSize: "0.875rem"
        }}
      >
        Cadastrar
      </Button>
    );
  };

  const HeaderComCards = () => {
    return (
      <Box sx={{ 
        display: "flex", 
        alignItems: "flex-end", 
        justifyContent: "center", 
        gap: 1.5, 
        flexWrap: "wrap",
        width: "100%"
      }}>
        {/* Cards de Resumo - Estatísticas */}
        <Grid container spacing={1.5} sx={{ flex: 1, minWidth: 0, maxWidth: "1440px" }}>
          <Grid item xs={6} sm={3}>
            <Card sx={{ 
              border: "1px solid #e0e0e0", 
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
              "&:hover": { boxShadow: "0 2px 6px rgba(0,0,0,0.15)" },
              height: "100%",
              display: "flex",
              flexDirection: "column"
            }}>
              <CardContent sx={{ 
                p: 1.5, 
                "&:last-child": { pb: 1.5 },
                display: "flex",
                flexDirection: "column",
                flex: 1,
                justifyContent: "space-between"
              }}>
                <Box>
                  <Typography variant="caption" sx={{ color: "#666", fontSize: "0.75rem" }}>
                    Total de Períodos
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: "#18316e", mt: 0.5 }}>
                    {estatisticas.totalPeriodos}
                  </Typography>
                </Box>
                <Typography variant="caption" sx={{ color: "#999", fontSize: "0.7rem", display: "block", mt: 0.5 }}>
                  {estatisticas.periodoMaisAntigo && estatisticas.periodoMaisRecente
                    ? `${estatisticas.periodoMaisAntigo} - ${estatisticas.periodoMaisRecente}`
                    : "Nenhum período cadastrado"}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={6} sm={3}>
            <Card sx={{ 
              border: "1px solid #e0e0e0", 
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
              "&:hover": { boxShadow: "0 2px 6px rgba(0,0,0,0.15)" },
              height: "100%",
              display: "flex",
              flexDirection: "column"
            }}>
              <CardContent sx={{ 
                p: 1.5, 
                "&:last-child": { pb: 1.5 },
                display: "flex",
                flexDirection: "column",
                flex: 1,
                justifyContent: "space-between"
              }}>
                <Box>
                  <Typography variant="caption" sx={{ color: "#666", fontSize: "0.75rem" }}>
                    Média Terceiros
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: "#18316e", mt: 0.5 }}>
                    {estatisticas.mediaTerceiros}%
                  </Typography>
                </Box>
                <Typography variant="caption" sx={{ color: "#999", fontSize: "0.7rem", display: "block", mt: 0.5 }}>
                  Média histórica
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={6} sm={3}>
            <Card sx={{ 
              border: "1px solid #e0e0e0", 
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
              "&:hover": { boxShadow: "0 2px 6px rgba(0,0,0,0.15)" },
              height: "100%",
              display: "flex",
              flexDirection: "column"
            }}>
              <CardContent sx={{ 
                p: 1.5, 
                "&:last-child": { pb: 1.5 },
                display: "flex",
                flexDirection: "column",
                flex: 1,
                justifyContent: "space-between"
              }}>
                <Box>
                  <Typography variant="caption" sx={{ color: "#666", fontSize: "0.75rem" }}>
                    Média RAT
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: "#18316e", mt: 0.5 }}>
                    {estatisticas.mediaRat}%
                  </Typography>
                </Box>
                <Typography variant="caption" sx={{ color: "#999", fontSize: "0.7rem", display: "block", mt: 0.5 }}>
                  Média histórica
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={6} sm={3}>
            <Card sx={{ 
              border: "1px solid #e0e0e0", 
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
              "&:hover": { boxShadow: "0 2px 6px rgba(0,0,0,0.15)" },
              height: "100%",
              display: "flex",
              flexDirection: "column"
            }}>
              <CardContent sx={{ 
                p: 1.5, 
                "&:last-child": { pb: 1.5 },
                display: "flex",
                flexDirection: "column",
                flex: 1,
                justifyContent: "space-between"
              }}>
                <Box>
                  <Typography variant="caption" sx={{ color: "#666", fontSize: "0.75rem" }}>
                    Média Total
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: "#18316e", mt: 0.5 }}>
                    {estatisticas.mediaTotal}%
                  </Typography>
                </Box>
                <Typography variant="caption" sx={{ color: "#999", fontSize: "0.7rem", display: "block", mt: 0.5 }}>
                  Terceiros + RAT + INSS
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    );
  };

  const HeaderBottomComBusca = () => {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <ComplementaryHeaderTop />
        <TextField
          sx={{
            width: "100%",
            maxWidth: "20rem",
            minWidth: "250px",
            "& .MuiOutlinedInput-root": {
              borderRadius: "50px",
              "& fieldset": {
                borderColor: "#18316e",
              },
              "&:hover fieldset": {
                borderColor: "#18316e",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#18316e",
              },
            },
          }}
          label="Referencia"
          size="small"
          value={searchValue}
          onChange={(e) => {
            setSearchValue(e.target.value);
            handleDebounce(e.target.value);
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "#18316e" }} />
              </InputAdornment>
            ),
          }}
        />
      </Box>
    );
  };

  return (
    <>
      {/* Cards em cima - área 20% maior que datatable */}
      <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
        <Box sx={{ width: "100%", maxWidth: "1440px", mt: "10px" }}>
          <HeaderComCards />
        </Box>
      </Box>
      
      {/* Datatable com largura padrão - botão e busca na mesma linha dos botões de visualização */}
      <Box sx={{ display: "flex", justifyContent: "center" }}>
        <Box sx={{ width: "100%", maxWidth: "1200px" }}>
          <TableCardv2
            data={dataTable}
            columns={columns}
            loading={loading}
            loadingVersao={loadingVersao}
            debounce={null}
            searchPlaceholder="Referencia"
            complementaryHeaderBottom={<HeaderBottomComBusca />}
          />
        </Box>
      </Box>
      <Dialog fullWidth open={openModalInsert} onClose={handleCloseModalInsert}>
        <DialogTitle>
          <h4>{isEdit ? "Atualizar" : "Inserir"} Imposto</h4>
        </DialogTitle>
        <DialogContent>
          <form onSubmit={isEdit ? handleSubmitUpdate : handleSubmitInsert}>
            <Grid container spacing={2} sx={{ marginBottom: "1rem" }}>
              {!isEdit && (
                <>
                  <Grid item xs={12} sm={6}>
                    <GenericDatePickerReference
                      label="Início"
                      value={referenciaInicio.content}
                      onChange={handleChangeReferenciaInicio}
                      sx={{ marginTop: "1rem" }}
                      disabled={!dados.isEditable}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <GenericDatePickerReference
                      label="Fim"
                      key={"referenciaFim"}
                      name="referenciaFim"
                      value={referenciaFim.content}
                      onChange={handleChangeReferenciaFim}
                      sx={{ marginTop: "1rem" }}
                      disabled={!dados.isEditable}
                    />
                  </Grid>
                </>
              )}
              {isEdit && (
                <Grid item xs={12} sm={6}>
                  <GenericDatePickerReference
                    label={`Referência`}
                    value={referencia}
                    onChange={(value) =>
                      setReferencia(dayjs(value).format("YYYY-MM"))
                    }
                    sx={{ marginTop: "1rem" }}
                    disabled={!dados.isEditable}
                  />
                </Grid>
              )}
              <Grid item xs={12} sm={6}>
                <TextField
                  error={terceiros.error}
                  value={terceiros.content}
                  onChange={(event) =>
                    handleChangeValue("terceiros", event.target.value)
                  }
                  fullWidth
                  size="small"
                  label="Terceiros"
                  sx={{ marginTop: "1rem" }}
                  disabled={!dados.isEditable}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">%</InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  error={rat.error}
                  value={rat.content}
                  onChange={(event) =>
                    handleChangeValue("rat", event.target.value)
                  }
                  fullWidth
                  size="small"
                  label="RAT"
                  sx={{ marginTop: "1rem" }}
                  disabled={!dados.isEditable}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">%</InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  error={inss.error}
                  value={inss.content}
                  onChange={(event) =>
                    handleChangeValue("inss", event.target.value)
                  }
                  fullWidth
                  size="small"
                  label="INSS"
                  sx={{ marginTop: "1rem" }}
                  disabled={!dados.isEditable}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">%</InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>
            <DialogActions
              sx={{
                width: "100%",
                justifyContent: "space-between",
              }}
            >
              <WhiteButton
                clickFunction={handleCloseModalInsert}
                textValue="Fechar"
                contentIcon={<CloseIcon />}
                iconLeft
              />

              <BlueButton
                textValue="Salvar"
                type="submit"
                contentIcon={<SendIcon />}
                disabled={!dados.isEditable}
              />
            </DialogActions>
          </form>
        </DialogContent>
      </Dialog>
      <Dialog open={openModalDelete} onClose={handleCloseModalDelete}>
        <DialogContent>
          <h2 style={{ fontSize: "1.1rem" }}>
            Deseja mesmo excluir esse Imposto?
          </h2>
          <div className={styles.containerButtons}>
            <WhiteButton
              textValue="Não"
              contentIcon={<CloseIcon />}
              clickFunction={handleCloseModalDelete}
              iconLeft
            />
            <BlueButton
              textValue="Sim"
              contentIcon={<CheckIcon />}
              clickFunction={handleSubmitDelete}
              disabled={!dados.isEditable}
            />
          </div>
        </DialogContent>
      </Dialog>

      <MensagemSucesso
        open={openSnackSucesso}
        msg={msg}
        handleClose={handleCloseSnackSucesso}
      />
      <MensagemErro
        open={openSnackErro}
        msg={msg}
        handleClose={handleCloseSnackErro}
      />
    </>
  );
});

export default Impostos;